import type React from "react";
import { useEffect, useState } from "react";
import * as Slider from "@radix-ui/react-slider";

const BPM_MIN = 20;
const BPM_MAX = 240;

type MobileBPMFilterProps = {
  value: [number, number];
  onChange: (next: [number, number]) => void;
};

export default function MobileBPMFilter({
  value,
  onChange,
}: MobileBPMFilterProps) {
  const [localMin, setLocalMin] = useState(value[0].toString());
  const [localMax, setLocalMax] = useState(value[1].toString());

  useEffect(() => {
    setLocalMin(value[0].toString());
    setLocalMax(value[1].toString());
  }, [value]);

  const handleBlur = () => {
    const min = clamp(parseInt(localMin, 10) || BPM_MIN, BPM_MIN, BPM_MAX);
    const max = clamp(parseInt(localMax, 10) || BPM_MAX, BPM_MIN, BPM_MAX);
    const nextMin = Math.min(min, max - 1);
    const nextMax = Math.max(max, nextMin + 1);
    onChange([nextMin, nextMax]);
  };

  return (
    <div className="space-y-4">
      <Slider.Root
        className="relative flex h-5 w-full touch-none select-none items-center"
        value={value}
        onValueChange={(next) =>
          onChange([next[0], next[1]] as [number, number])
        }
        min={BPM_MIN}
        max={BPM_MAX}
        step={1}
        minStepsBetweenThumbs={1}
      >
        <Slider.Track className="relative h-[5px] w-full grow rounded-full bg-white/20">
          <Slider.Range className="absolute h-full rounded-full bg-[#43DDA7] shadow-[0_0_10px_rgba(67,221,167,0.5)]" />
        </Slider.Track>
        <Slider.Thumb className={thumbClassName} />
        <Slider.Thumb className={thumbClassName} />
      </Slider.Root>

      <div className="flex items-center gap-4">
        <NumberInput
          label="Min BPM"
          value={localMin}
          onChange={(event) =>
            setLocalMin(event.target.value.replace(/[^\d]/g, ""))
          }
          onBlur={handleBlur}
        />
        <span className="text-white/50 font-semibold">—</span>
        <NumberInput
          label="Max BPM"
          value={localMax}
          onChange={(event) =>
            setLocalMax(event.target.value.replace(/[^\d]/g, ""))
          }
          onBlur={handleBlur}
        />
      </div>
    </div>
  );
}

type NumberInputProps = {
  label: string;
  value: string;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur: () => void;
};

function NumberInput({ label, value, onChange, onBlur }: NumberInputProps) {
  return (
    <label className="flex flex-col text-[12px] font-semibold text-white/60 flex-1">
      {label}
      <input
        type="text"
        inputMode="numeric"
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        className="mt-1 h-[40px] w-full rounded-lg border border-white/15 bg-black px-3 text-[15px] font-semibold text-white focus:border-white/40 focus:outline-none"
      />
    </label>
  );
}

const thumbClassName =
  "block h-5 w-5 rounded-full border-2 border-white bg-black shadow-[0_2px_10px_rgba(0,0,0,0.4)] focus:outline-none focus-visible:ring-2 focus-visible:ring-white";

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}
